/**
 * Pattern Result discriminé — type-safe, zéro exception levée.
 * Ce fichier sera enrichi par vp-implementer B2.
 *
 * Usage :
 *   const res = ok(value);
 *   const res = err({ code: 'NOT_FOUND', message: '...' });
 *   if (res.ok) { res.value } else { res.error }
 */

export type Result<T, E> =
  | { readonly ok: true; readonly value: T }
  | { readonly ok: false; readonly error: E };

/** Construit un résultat succès. */
export function ok<T>(value: T): Result<T, never> {
  return { ok: true, value };
}

/** Construit un résultat erreur. */
export function err<E>(error: E): Result<never, E> {
  return { ok: false, error };
}

/**
 * Extrait l'erreur d'un Result. Nécessaire car `strictNullChecks: false` (tsconfig)
 * casse le narrowing des unions discriminées sur littéraux booléens : dans un bloc
 * `if (!result.ok) { ... }`, TS ne narrow PAS `result.error` (alors qu'il narrow bien
 * `result.value` après le bloc). Ce helper isole l'unique cast nécessaire.
 * ⚠ N'appeler QUE dans une branche où `result.ok === false` est garanti.
 */
export function errOf<T, E>(result: Result<T, E>): E {
  return (result as { readonly ok: false; readonly error: E }).error;
}

/** Type-guard succès. */
export function isOk<T, E>(result: Result<T, E>): result is { ok: true; value: T } {
  return result.ok === true;
}

/** Type-guard erreur. */
export function isErr<T, E>(result: Result<T, E>): result is { ok: false; error: E } {
  return result.ok === false;
}

/**
 * Transforme la valeur d'un Result succès via fn.
 * Laisse passer les erreurs inchangées.
 */
export function mapOk<T, U, E>(result: Result<T, E>, fn: (value: T) => U): Result<U, E> {
  if (result.ok) {
    return ok(fn(result.value));
  }
  // strictNullChecks off → result n'est pas narrowé en {ok:false} ici ; on
  // reconstruit l'erreur (même valeur) pour rester assignable à Result<U, E>.
  return err(errOf(result));
}

/**
 * Chaîne deux opérations qui retournent toutes deux un Result.
 * Court-circuite sur la première erreur.
 */
export function flatMap<T, U, E>(
  result: Result<T, E>,
  fn: (value: T) => Result<U, E>,
): Result<U, E> {
  if (result.ok) {
    return fn(result.value);
  }
  return err(errOf(result));
}

/**
 * Extrait la valeur d'un Result ou retourne fallback si erreur.
 */
export function unwrapOr<T, E>(result: Result<T, E>, fallback: T): T {
  if (result.ok) {
    return result.value;
  }
  return fallback;
}
